import React, { useState, useRef, useEffect, useMemo } from "react";
import "./PartnersPage.scss";
import { usePartnersViewModel } from "../viewmodel/usePartnersViewModel";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { FiltersDrawer } from "../components/FiltersDrawer";
import { SelectedFilters } from "../components/SelectedFilters";
import { PartnerItem } from "../components/PartnerItem";
import { MemberCard } from "../components/MemberCard";
import Loader from "@/core/components/loader/Loader";
import { useUnit } from "effector-react";
import { toggleMemberFollowFx } from "../store";
import { $userBasicInfo } from "@/features/user/store";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import { setModalIsOpened } from "@/core/store/app";
import { useLocation, useNavigate } from "react-router-dom";
import Tracking from "@/core/utils/tracking";
import { SEO } from "@/core/components/SEO/SEO";
import { ROUTES } from "@/core/constants/app";

export type PartnersFilter = "all" | "followed" | "my_network";
type NetworkTab = "followers" | "following";

export const PartnersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveFilterFromPath = (): PartnersFilter => {
    switch (location.pathname) {
      case ROUTES.PARTNERS_ORGANIZATIONS:
        return "all";
      case ROUTES.PARTNERS_MEMBERS:
        return "my_network";
      case ROUTES.PARTNERS_MY_NETWORK:
        return "followed";
      default:
        return "all";
    }
  };

  const [activeFilter, setActiveFilter] = useState<PartnersFilter>(
    getActiveFilterFromPath(),
  );
  const [activeNetworkTab, setActiveNetworkTab] =
    useState<NetworkTab>("followers");
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(
    new Set(),
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState({
    countries: [],
    sectors: [],
    programmes: [],
    organizationCategories: [],
    companySize: [],
    legalForm: [],
    expertise: [],
  });
  const loadMoreButtonRef = useRef<HTMLButtonElement>(null);

  const {
    loading,
    error,
    getFilteredPartners,
    getAllPartnersCount,
    getTotalPartnersCount,
    fetchPartnersWithFilters,
    loadMorePartnersWithFilters,
    hasMorePartners,
    loadMore,
    fetchFilteredData,
    filterLoading,
    loadMoreUsersLoading,
    filteredPartners,
    filteredUsers,
    currentFilter,
    fetchNetworkTabData,
    followersCount,
    followingCount,
    searchUsers,
    fetchAllUsers,
    fetchUsersWithFilters,
    loadMoreUsers,
    usersPagination,
    partnersState,
  } = usePartnersViewModel();
  const [toggleMemberFollow, userBasicInfo] = useUnit([
    toggleMemberFollowFx,
    $userBasicInfo,
  ]);
  const { pro_user } = userBasicInfo || {};

  useEffect(() => {
    if (location.pathname === ROUTES.PARTNERS) {
      navigate(ROUTES.PARTNERS_ORGANIZATIONS, { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const newActiveFilter = getActiveFilterFromPath();
    setActiveFilter(newActiveFilter);
    setHasSearched(false);
    setSearchQuery("");
    setAppliedFilters({
      countries: [],
      sectors: [],
      programmes: [],
      organizationCategories: [],
      companySize: [],
      legalForm: [],
      expertise: [],
    });
    fetchFilteredData(newActiveFilter);
  }, [location.pathname, fetchFilteredData]);

  useEffect(() => {
    Tracking.pageView(
      "Partners",
      location.pathname,
      undefined,
      undefined,
      undefined,
      document.title,
      undefined,
      undefined,
    );
  }, [location.pathname]);

  const getActiveFiltersCount = useMemo(() => {
    let count = 0;
    count += appliedFilters.countries.length;
    count += appliedFilters.programmes.length;
    count += appliedFilters.sectors.length;
    count += appliedFilters.expertise.length;
    count += appliedFilters.organizationCategories.length;
    count += appliedFilters.companySize.length;
    count += appliedFilters.legalForm.length;
    return count;
  }, [appliedFilters]);

  const handleFilterChange = (filter: PartnersFilter) => {
    let route: string = ROUTES.PARTNERS_ORGANIZATIONS;
    if (filter === "my_network") {
      route = ROUTES.PARTNERS_MEMBERS;
    } else if (filter === "followed") {
      route = ROUTES.PARTNERS_MY_NETWORK;
    }
    navigate(route);
  };

  const handleMemberFollow = async (memberId: string, isFollowing: boolean) => {
    try {
      await toggleMemberFollow({ memberId, isFollowing });
      if (currentFilter === "followed" || currentFilter === "my_network") {
        if (searchQuery) {
          await searchUsers(searchQuery);
        } else {
          await fetchFilteredData(currentFilter);
        }
      }
    } catch (error) {
      console.error("Error toggling member follow:", error);
    }
  };

  const handleBookmarkAction = async (
    partnerId: string,
    isBookmarked: boolean,
  ) => {
    // console.log(
      // "Bookmark action for partner:",
      // partnerId,
      // "isBookmarked:",
      // isBookmarked,
    // );
  };

  const handleNetworkTabChange = async (tab: NetworkTab) => {
    setActiveNetworkTab(tab);
    await fetchNetworkTabData(tab);
  };

  const createFilterParams = (filters: typeof appliedFilters) => {
    if (activeFilter === "my_network") {
      return {
        search: searchQuery || undefined,
        countries:
          filters.countries.length > 0
            ? filters.countries.join(",")
            : undefined,
        sectors:
          filters.sectors.length > 0 ? filters.sectors.join(",") : undefined,
        expertise:
          filters.expertise.length > 0
            ? filters.expertise.join(",")
            : undefined,
      };
    } else {
      return {
        search: searchQuery || undefined,
        countries:
          filters.countries.length > 0
            ? filters.countries.join(",")
            : undefined,
        sectors:
          filters.sectors.length > 0 ? filters.sectors.join(",") : undefined,
        programmes:
          filters.programmes.length > 0
            ? filters.programmes.join(",")
            : undefined,
        organization_categories:
          filters.organizationCategories.length > 0
            ? filters.organizationCategories.join(",")
            : undefined,
        company_size:
          filters.companySize.length > 0
            ? filters.companySize.join(",")
            : undefined,
        legal_form:
          filters.legalForm.length > 0
            ? filters.legalForm.join(",")
            : undefined,
      };
    }
  };

  const toggleSectorExpansion = (partnerId: string) => {
    setExpandedSectors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(partnerId)) {
        newSet.delete(partnerId);
      } else {
        newSet.add(partnerId);
      }
      return newSet;
    });
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setHasSearched(true);
      if (activeFilter === "my_network" && searchQuery) {
        searchUsers(searchQuery);
      } else {
        fetchPartnersWithFilters({ search: searchQuery || undefined });
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value) {
      setHasSearched(false);
    }
  };

  const handleLoadMore = () => {
    loadMorePartnersWithFilters(createFilterParams(appliedFilters));
  };

  const handleLoadMoreUsers = () => {
    loadMoreUsers(createFilterParams(appliedFilters));
  };

  const handleFiltersApplied = (filters: any) => {
    setAppliedFilters(filters);
    setHasSearched(true);
    if (activeFilter === "my_network") {
      fetchUsersWithFilters(createFilterParams(filters));
    } else {
      fetchPartnersWithFilters(createFilterParams(filters));
    }
  };

  const handleRemoveFilter = (category: string, value: string) => {
    const newFilters = {
      ...appliedFilters,
      [category]: appliedFilters[
        category as keyof typeof appliedFilters
      ].filter((item: string) => item !== value),
    };
    setAppliedFilters(newFilters);
    if (activeFilter === "my_network") {
      fetchUsersWithFilters(createFilterParams(newFilters));
    } else {
      fetchPartnersWithFilters(createFilterParams(newFilters));
    }
  };

  const handleClearAllFilters = () => {
    const clearedFilters = {
      countries: [],
      sectors: [],
      programmes: [],
      organizationCategories: [],
      companySize: [],
      legalForm: [],
      expertise: [],
    };
    setAppliedFilters(clearedFilters);
    if (!searchQuery) {
      setHasSearched(false);
    }
    const baseFilters = { search: searchQuery || undefined };
    if (activeFilter === "my_network") {
      fetchUsersWithFilters(baseFilters);
    } else {
      fetchPartnersWithFilters(baseFilters);
    }
  };

  if (loading || filterLoading) {
    return (
      <div className="partners-page">
        <div className="pageTitle">Partners</div>
        <Loader overlay />
      </div>
    );
  }

  if (error) {
    return (
      <div className="partners-page">
        <div className="pageTitle">Partners</div>
        <div>Error loading partners: {error}</div>
      </div>
    );
  }

  const hasActiveFilters = Object.values(appliedFilters).some(
    (arr) => arr.length > 0,
  );
  const hasActiveSearch = searchQuery.length > 0;
  const shouldShowFilteredResults =
    hasActiveSearch || hasActiveFilters || hasSearched;
  const displayedPartners = shouldShowFilteredResults
    ? filteredPartners
    : partnersState.partners;
  const displayedUsers = filteredUsers;

  return (
    <div className="partners-page">
      <SEO title={`Partners | EUcalls`} description={"Partners"} />

      <div className="pageTitle">Partners</div>
      <div className="partners-header">
        <div className="button-group">
          <button
            className={`button-group__item ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => handleFilterChange("all")}
          >
            Organizations (
            {partnersState.partnersGeneralData.total_organizations})
          </button>

          <button
            className={`button-group__item ${activeFilter === "my_network" ? "active" : ""}`}
            onClick={() => handleFilterChange("my_network")}
          >
            Members ({partnersState.partnersGeneralData.total_members})
          </button>
          <button
            className={`button-group__item ${activeFilter === "followed" ? "active" : ""}`}
            onClick={() => handleFilterChange("followed")}
          >
            My Network
          </button>
        </div>
      </div>

      <div className="partner-filters">
        <div className="filter-search">
          <input
            type="text"
            placeholder="Search"
            className="search-input"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchSubmit}
          />
        </div>

        <div className="filter-controls">
          {(activeFilter === "all" || activeFilter === "my_network") && (
            <button
              className="filter-btn filters-btn"
              onClick={() => setIsFiltersDrawerOpen(true)}
            >
              <SvgIcon icon="icon-filter" />
              Filters
              {getActiveFiltersCount > 0 && (
                <span className="filter-count">{getActiveFiltersCount}</span>
              )}
            </button>
          )}
          {activeFilter === "followed" && (
            <div className="network-tabs">
              <div className="button-group">
                <button
                  className={`button-group__item ${activeNetworkTab === "followers" ? "active" : ""}`}
                  onClick={() => handleNetworkTabChange("followers")}
                >
                  Followers ({followersCount})
                </button>
                <button
                  className={`button-group__item ${activeNetworkTab === "following" ? "active" : ""}`}
                  onClick={() => handleNetworkTabChange("following")}
                >
                  Following ({followingCount})
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {(activeFilter === "all" || activeFilter === "my_network") && (
        <SelectedFilters
          filters={appliedFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />
      )}

      <div className="partners-results">
        {activeFilter === "all" && getTotalPartnersCount()}
        {activeFilter == "my_network" && usersPagination.count} results
      </div>

      <div className="partners-content">
        <div
          className={
            activeFilter === "followed" || activeFilter === "my_network"
              ? `partners-list grid-view-3`
              : `partners-list grid-view-2`
          }
        >
          {activeFilter === "followed" || activeFilter === "my_network"
            ? displayedUsers.map((user) => (
                <MemberCard
                  key={user.id}
                  type="user"
                  member={user}
                  onMemberFollow={handleMemberFollow}
                />
              ))
            : getTotalPartnersCount() > 0 &&
              displayedPartners.map((partner) => (
                <PartnerItem
                  key={partner.id}
                  partner={partner}
                  expandedSectors={expandedSectors}
                  onToggleSectorExpansion={toggleSectorExpansion}
                />
              ))}
        </div>

        {shouldShowFilteredResults &&
          displayedPartners.length === 0 &&
          displayedUsers.length === 0 && (
            <div className="empty-search-results">
              <div className="empty-state-icon">
                <SvgIcon icon="icon-search" />
              </div>
              <h3>No results found</h3>
              <p>
                {searchQuery
                  ? `No partners found for "${searchQuery}"`
                  : "No partners found matching your filters"}
              </p>
              <button
                className="clear-search-btn"
                onClick={() => {
                  setSearchQuery("");
                  setHasSearched(false);
                  setAppliedFilters({
                    countries: [],
                    sectors: [],
                    programmes: [],
                    organizationCategories: [],
                    companySize: [],
                    legalForm: [],
                    expertise: [],
                  });
                  fetchFilteredData(activeFilter);
                }}
              >
                Clear search and filters
              </button>
            </div>
          )}
        {activeFilter === "all" &&
          getTotalPartnersCount() > 0 &&
          displayedPartners.length > 0 && (
            <div className="partners-action">
              {loadMore ? (
                <Loader message="Loading" />
              ) : (
                <button
                  ref={loadMoreButtonRef}
                  className="partners-loadMore"
                  onClick={handleLoadMore}
                  disabled={loadMore}
                >
                  Load more
                </button>
              )}
            </div>
          )}
        {activeFilter === "my_network" &&
          usersPagination.hasMore &&
          displayedUsers.length > 0 && (
            <div className="partners-action">
              {loadMoreUsersLoading ? (
                <Loader message="Loading" />
              ) : (
                <button
                  ref={loadMoreButtonRef}
                  className="partners-loadMore"
                  onClick={handleLoadMoreUsers}
                  disabled={loadMoreUsersLoading}
                >
                  Load more
                </button>
              )}
            </div>
          )}
      </div>

      <FiltersDrawer
        isOpen={isFiltersDrawerOpen}
        type={activeFilter}
        onClose={() => setIsFiltersDrawerOpen(false)}
        onFiltersApplied={handleFiltersApplied}
        appliedFilters={appliedFilters}
      />

      <CommonModal
        onClose={() => {
          setShowProModal(false);
          setModalIsOpened(false);
        }}
        hasForm={false}
        width={430}
        height={440}
        icon={"icon-lighting"}
        isOpen={showProModal}
        actions={
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowProModal(false);
                setModalIsOpened(false);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-idea-form"
              className="btn-primary"
              onClick={() => navigate("/settings/subscription")}
            >
              View plans
            </button>
          </div>
        }
      >
        <div className="proModal">
          <div className="proModal-title">
            Get full access to Pro features with a paid plan
          </div>
          <div className="proModal-description flex flex-col gap-1">
            You could do so much more with our Professional and Premium plans.
            Unlock features like:
            <div className="proModal-list">
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" /> My Network followers and following
              </div>
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" />
                Advanced member connections
              </div>
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" />
                Network management tools
              </div>
            </div>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};
